// Byrån godkänner de allmänna villkoren och biträdesavtalet innan kontot kan
// användas (ADR 0100). Båda rutorna måste kryssas i. Bara ägaren kan godkänna.
import { AGREEMENT_KINDS, type AgreementKind } from '@leadledger/domain';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { ApiFel, data } from '../../api/client.js';
import { useAuth, useMe } from '../../auth/auth.js';
import { Marke } from '../../layout/ikoner.js';
import { AGREEMENTS } from './texter.js';

export function GodkannAvtal() {
  const me = useMe();
  const { api, loggaUt } = useAuth();
  const klient = useQueryClient();
  const agare = me.user.role === 'owner';
  const [kryss, setKryss] = useState<Record<AgreementKind, boolean>>({ terms: false, dpa: false });
  const godkann = useMutation({
    mutationFn: async () =>
      data(
        await api.POST('/v1/admin/agreements/accept', {
          body: { terms: AGREEMENTS.terms.version, dpa: AGREEMENTS.dpa.version },
        }),
      ),
    onSuccess: () => klient.invalidateQueries({ queryKey: ['me'] }),
  });
  const allaKryssade = AGREEMENT_KINDS.every((k) => kryss[k]);

  function skicka(e: FormEvent) {
    e.preventDefault();
    if (allaKryssade) godkann.mutate();
  }

  return (
    <div className="kom-igang">
      <header className="kom-igang-huvud">
        <span className="marke">
          <Marke />
          LeadLedger
        </span>
        <button type="button" className="btn btn-s" onClick={loggaUt}>
          Logga ut
        </button>
      </header>
      <main id="innehall" className="kom-igang-sida">
        <div>
          <h1>Villkor och biträdesavtal</h1>
          <p className="ingress">
            Innan {me.tenant.name} börjar använda LeadLedger behöver byrån godkänna de allmänna
            villkoren och biträdesavtalet. Biträdesavtalet beskriver hur vi behandlar era kunders
            personuppgifter åt er.
          </p>
        </div>
        {agare ? (
          <form onSubmit={skicka} className="kort avtal-form">
            {AGREEMENT_KINDS.map((k) => (
              <label key={k} className="avtal-kryss">
                <input
                  type="checkbox"
                  required
                  checked={kryss[k]}
                  onChange={(e) => setKryss({ ...kryss, [k]: e.target.checked })}
                />
                <span>
                  Jag har läst och godkänner{' '}
                  <Link to={AGREEMENTS[k].path} target="_blank" rel="noopener">
                    {k === 'terms' ? 'de allmänna villkoren' : 'biträdesavtalet'}
                  </Link>{' '}
                  (version {AGREEMENTS[k].version}) för {me.tenant.name}.
                </span>
              </label>
            ))}
            {godkann.isError && (
              <div className="meddelande" role="alert">
                {godkann.error instanceof ApiFel && godkann.error.status === 409
                  ? 'Avtalet har en ny version. Ladda om sidan, läs texten och godkänn igen.'
                  : 'Det gick inte att spara godkännandet. Försök igen om en stund.'}
              </div>
            )}
            <div className="kom-igang-fot">
              <button
                type="submit"
                className="btn btn-p"
                disabled={!allaKryssade || godkann.isPending}
              >
                {godkann.isPending ? 'Sparar …' : 'Godkänn och fortsätt'}
              </button>
            </div>
          </form>
        ) : (
          <div className="kort notis" role="note">
            Byråns ägare behöver godkänna villkoren och biträdesavtalet innan kontot kan användas.
            Be ägaren att logga in. Du kan läsa{' '}
            <Link to={AGREEMENTS.terms.path}>de allmänna villkoren</Link> och{' '}
            <Link to={AGREEMENTS.dpa.path}>biträdesavtalet</Link>.
          </div>
        )}
      </main>
    </div>
  );
}
